import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { trackEvent } from "@/lib/site";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
const choices: Record<number, {
  title: string;
  field: keyof FormState;
  values: {
    label: string;
    value: string;
  }[];
}> = {
  1: {
    title: "Wofür interessierst du dich?",
    field: "interest",
    values: [{
      label: "Firmengründung",
      value: "firmengruendung"
    }, {
      label: "Konto",
      value: "konto"
    }, {
      label: "Firma + Konto",
      value: "firma-konto"
    }, {
      label: "Relocation",
      value: "relocation"
    }, {
      label: "Investment",
      value: "investment"
    }, {
      label: "Stay",
      value: "stay"
    }, {
      label: "Mehrere Themen",
      value: "mehrere-themen"
    }]
  },
  2: {
    title: "Wann möchtest du starten?",
    field: "timeframe",
    values: [{
      label: "Sofort",
      value: "sofort"
    }, {
      label: "1–3 Monate",
      value: "1-3-monate"
    }, {
      label: "3–6 Monate",
      value: "3-6-monate"
    }, {
      label: "Erst informieren",
      value: "informieren"
    }]
  },
  4: {
    title: "Besteht bereits ein Unternehmen?",
    field: "existingCompany",
    values: [{
      label: "Ja",
      value: "ja"
    }, {
      label: "Nein",
      value: "nein"
    }]
  },
  5: {
    title: "Interessieren dich auch Investments?",
    field: "investmentInterest",
    values: [{
      label: "Ja",
      value: "ja"
    }, {
      label: "Nein",
      value: "nein"
    }, {
      label: "Vielleicht",
      value: "vielleicht"
    }]
  }
};
type FormState = {
  interest: string;
  timeframe: string;
  businessActivity: string;
  existingCompany: string;
  investmentInterest: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsapp: string;
  consent: boolean;
};
const initial: FormState = {
  interest: "",
  timeframe: "",
  businessActivity: "",
  existingCompany: "",
  investmentInterest: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  whatsapp: "",
  consent: false
};
export function SetupWizard() {
  const {
    tx,
    tf,
    href: localeHref,
    dateLocale
  } = useLanguage();
  const [step, setStep] = useState(1);
  const [startedAt] = useState(() => Date.now());
  const [data, setData] = useState<FormState>(initial);
  const [success, setSuccess] = useState(false);
  const submit = trpc.public.submitLead.useMutation();
  const choice = choices[step];
  const canContinue = step === 3 ? data.businessActivity.trim().length >= 3 : step === 6 ? data.firstName.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(data.email) && data.consent : Boolean(data[choice?.field] ?? "");
  function choose(field: keyof FormState, value: string) {
    setData(current => ({
      ...current,
      [field]: value
    }));
  }
  async function finish() {
    try {
      await submit.mutateAsync({
        kind: "setup",
        firstName: data.firstName,
        lastName: data.lastName || undefined,
        email: data.email,
        phone: data.phone || undefined,
        whatsapp: data.whatsapp || undefined,
        interest: data.interest,
        timeframe: data.timeframe,
        businessActivity: data.businessActivity,
        existingCompany: data.existingCompany,
        investmentInterest: data.investmentInterest,
        consent: true,
        source: window.location.pathname,
        utmSource: new URLSearchParams(window.location.search).get("utm_source") ?? undefined,
        utmMedium: new URLSearchParams(window.location.search).get("utm_medium") ?? undefined,
        utmCampaign: new URLSearchParams(window.location.search).get("utm_campaign") ?? undefined,
        startedAt
      });
      trackEvent("setup_check_completed", {
        interest: data.interest,
        timeframe: data.timeframe
      });
      setSuccess(true);
    } catch (error) {
      toast.error(tx(error instanceof Error ? error.message : "Der Setup-Check konnte nicht gesendet werden."));
    }
  }
  if (success) return <div className="mx-auto max-w-3xl border border-[#C4A46B]/45 bg-[#FFFDF9] p-10 text-center sm:p-16"><span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#A98950] text-white"><Check /></span><h2 className="mt-7 display-md font-semibold">{tx("DANKE. WIR PRÜFEN DEIN VORHABEN.")}</h2><p className="mx-auto mt-5 max-w-xl leading-8 text-[#66615A]">{tx("Deine Angaben sind sicher eingegangen. Unser Team meldet sich persönlich, um offene Fragen und den passenden nächsten Schritt zu besprechen.")}</p></div>;
  return <div className="mx-auto max-w-4xl border border-[#DED6C8] bg-[#FFFDF9] shadow-[0_28px_100px_rgba(48,36,18,.08)]">
      <div className="border-b border-[#DED6C8] px-6 py-5 sm:px-10">
        <div className="flex items-center justify-between text-[10px] font-bold tracking-[.18em] text-[#66615A]"><span>{tx("SETUP-CHECK")}</span><span>{tx("SCHRITT ")}{tx(step)} / 6</span></div>
        <div className="mt-4 h-1 bg-[#EEE7DB]"><div className="h-full bg-[#A98950] transition-[width] duration-300" style={{
          width: `${step / 6 * 100}%`
        }} /></div>
      </div>
      <div className="min-h-[430px] p-6 sm:p-10 lg:p-14">
        {tx(choice && <><p className="eyebrow">{tx("DEIN VORHABEN")}</p><h2 className="mt-3 text-4xl font-semibold sm:text-5xl">{tx(choice.title)}</h2><div className="mt-8 grid gap-3 sm:grid-cols-2">{tx(choice.values.map(item => {
            const selected = data[choice.field] === item.value;
            return <button type="button" key={item.value} onClick={() => choose(choice.field, item.value)} className={`flex items-center justify-between border p-5 text-left font-semibold ${selected ? "border-[#A98950] bg-[#EEE7DB]" : "border-[#DED6C8] bg-white hover:border-[#A98950]"}`}><span>{tx(item.label)}</span>{tx(selected && <Check className="h-4 w-4 text-[#A98950]" />)}</button>;
          }))}</div></>)}
        {tx(step === 3 && <><p className="eyebrow">{tx("KURZ BESCHRIEBEN")}</p><h2 className="mt-3 text-4xl font-semibold sm:text-5xl">{tx("Welche geschäftliche Tätigkeit planst du?")}</h2><Textarea value={data.businessActivity} onChange={event => setData(current => ({
          ...current,
          businessActivity: event.target.value
        }))} rows={7} className="mt-8 rounded-none bg-white text-base" placeholder={tx("Branche, Ziel und aktueller Stand – ein paar Sätze genügen.")} /></>)}
        {tx(step === 6 && <><p className="eyebrow">{tx("PERSÖNLICHER KONTAKT")}</p><h2 className="mt-3 text-4xl font-semibold sm:text-5xl">{tx("Wie dürfen wir dich erreichen?")}</h2><div className="mt-8 grid gap-5 sm:grid-cols-2"><div><Label htmlFor="firstName">{tx("Vorname *")}</Label><Input id="firstName" value={data.firstName} onChange={e => setData(v => ({
              ...v,
              firstName: e.target.value
            }))} className="mt-2 rounded-none bg-white" /></div><div><Label htmlFor="lastName">{tx("Nachname")}</Label><Input id="lastName" value={data.lastName} onChange={e => setData(v => ({
              ...v,
              lastName: e.target.value
            }))} className="mt-2 rounded-none bg-white" /></div><div><Label htmlFor="email">{tx("E-Mail *")}</Label><Input id="email" type="email" value={data.email} onChange={e => setData(v => ({
              ...v,
              email: e.target.value
            }))} className="mt-2 rounded-none bg-white" /></div><div><Label htmlFor="phone">{tx("Telefon")}</Label><Input id="phone" value={data.phone} onChange={e => setData(v => ({
              ...v,
              phone: e.target.value
            }))} className="mt-2 rounded-none bg-white" /></div><div className="sm:col-span-2"><Label htmlFor="whatsapp">{tx("WhatsApp")}</Label><Input id="whatsapp" value={data.whatsapp} onChange={e => setData(v => ({
              ...v,
              whatsapp: e.target.value
            }))} className="mt-2 rounded-none bg-white" /></div></div><label className="mt-6 flex items-start gap-3 text-xs leading-5 text-[#66615A]"><Checkbox checked={data.consent} onCheckedChange={value => setData(v => ({
            ...v,
            consent: value === true
          }))} className="mt-0.5" /><span>{tx("Ich stimme der Verarbeitung meiner Angaben zur Bearbeitung dieser Anfrage gemäß der Datenschutzerklärung zu. *")}</span></label></>)}
      </div>
      <div className="flex items-center justify-between border-t border-[#DED6C8] px-6 py-5 sm:px-10">
        <Button variant="ghost" onClick={() => setStep(value => Math.max(1, value - 1))} disabled={step === 1} className="rounded-none"><ArrowLeft />{tx(" ZURÜCK")}</Button>
        {tx(step < 6 ? <Button onClick={() => setStep(value => value + 1)} disabled={!canContinue} className="rounded-none bg-[#A98950] px-7 text-white hover:bg-[#91713d]">{tx("WEITER ")}<ArrowRight /></Button> : <Button onClick={finish} disabled={!canContinue || submit.isPending} className="rounded-none bg-[#A98950] px-7 text-white hover:bg-[#91713d]">{tx(submit.isPending ? <Loader2 className="animate-spin" /> : <>{tx("ABSENDEN ")}<ArrowRight /></>)}</Button>)}
      </div>
    </div>;
}
